import { SCRAPER_NOTE } from "@/lib/odds-screen/automation";

export type OtpAction = "send" | "verify" | "resend";

export type OtpErrorCategory =
  | "banned"
  | "user_not_found"
  | "cooldown"
  | "rate_limited"
  | "code_expired"
  | "invalid_code"
  | "too_many_attempts"
  | "network"
  | "unknown";

const COOLDOWN_PATTERN = /you can only request this after(?: (\d+) seconds?)?/i;

const CATEGORY_RULES: Array<{
  category: OtpErrorCategory;
  test: (message: string) => boolean;
}> = [
  { category: "banned", test: (message) => /user is banned/i.test(message) },
  { category: "cooldown", test: (message) => COOLDOWN_PATTERN.test(message) },
  { category: "rate_limited", test: (message) => /rate limit/i.test(message) },
  {
    category: "user_not_found",
    test: (message) => /not found|signups not allowed|otp_disabled/i.test(message),
  },
  { category: "code_expired", test: (message) => /expired/i.test(message) },
  { category: "invalid_code", test: (message) => /invalid/i.test(message) },
  { category: "too_many_attempts", test: (message) => /too many/i.test(message) },
  {
    category: "network",
    test: (message) => /load failed|failed to fetch|networkerror|network request failed/i.test(message),
  },
];

export function categorizeOtpError(message: string | null | undefined): OtpErrorCategory {
  const normalized = typeof message === "string" ? message.trim() : "";
  if (!normalized) return "unknown";
  return CATEGORY_RULES.find((rule) => rule.test(normalized))?.category ?? "unknown";
}

export function otpCooldownSeconds(message: string | null | undefined): number | null {
  if (typeof message !== "string") return null;
  const match = COOLDOWN_PATTERN.exec(message);
  if (!match?.[1]) return null;
  const seconds = Number(match[1]);
  return Number.isFinite(seconds) ? seconds : null;
}

export function otpErrorCopy(
  category: OtpErrorCategory,
  action: OtpAction,
  message?: string | null,
): string {
  if (category === "banned") return SCRAPER_NOTE;
  if (category === "user_not_found") {
    return "No account found with this email. Sign up first.";
  }
  if (category === "cooldown") {
    const seconds = otpCooldownSeconds(message);
    return seconds === null
      ? "Wait a moment and try again."
      : `Wait ${seconds} seconds and try again.`;
  }
  if (category === "rate_limited" || category === "too_many_attempts") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (category === "code_expired") {
    return "Code expired. Request a new one.";
  }
  if (category === "invalid_code") {
    return "Invalid code. Check it and try again.";
  }
  if (category === "network") {
    return "Couldn't reach sign-in. Check your connection and try again.";
  }
  if (action === "verify") return "Couldn't verify the code. Try again.";
  if (action === "resend") return "Couldn't resend the code. Try again.";
  return "Couldn't send the code. Try again.";
}
